/**
 * Copy plain text. `navigator.clipboard` is secure-context only, so over plain
 * http on a LAN / Tailscale address it is undefined and every "Copy link" did
 * nothing; the hidden-textarea + `execCommand("copy")` path still works there.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** YouTrack's "Copy ID and summary to clipboard": plain text `KEY summary`, and as rich text the key is a link to the issue. */
export async function copyIdAndSummary(key: string, summary: string, url: string): Promise<void> {
  const text = `${key} ${summary}`;
  const esc = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const html = `<a href="${esc(url)}">${esc(key)}</a> ${esc(summary)}`;
  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": new Blob([text], { type: "text/plain" }), "text/html": new Blob([html], { type: "text/html" }) })]);
      return;
    }
  } catch {
    /* fall through to plain text */
  }
  await copyText(text);
}

/** ⇧⌥⌘C on a Mac, ⇧Alt+Ctrl+C elsewhere — the shortcut YouTrack shows in the tooltip. */
export function isCopyIssueShortcut(e: KeyboardEvent): boolean {
  return e.code === "KeyC" && e.shiftKey && e.altKey && (e.metaKey || e.ctrlKey);
}

export const COPY_SHORTCUT_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? "⇧⌥⌘C" : "Shift+Alt+Ctrl+C";
